/**
 * Apollo Link: timingLink
 *
 * Purpose:
 * - Measures and tracks the duration of each outgoing GraphQL operation.
 * - Builds a Snowplow DurationEvent (`graphql_query`) payload immediately on completion.
 * - Buffers events only if the page has not fully loaded (initial render).
 * - Sends all buffered events once the page is in a valid state.
 * - Enriches GraphQL events with ConditionEntities based on query variables.
 *
 * How it works:
 * - On each GraphQL request, records a `start` timestamp.
 * - When the request returns (success or error), calculates the total duration.
 * - Creates a DurationEvent payload and builds ConditionEntities.
 * - If the page is still loading, it stores the event in `pendingEvents`.
 * - If the page is loaded, it immediately fires the event with available contexts.
 * - Remaining buffered events are flushed later via `flushPendingTimingEvents`.
 *
 * Notes:
 * - Ensures accurate timing even if page transitions are incomplete.
 * - Prevents misattribution of requests to the wrong page.
 * - Attaches PageEntity, ConditionEntities from URL, and ConditionEntities from GraphQL variables to every event.
 */

import { ApolloLink, Observable } from '@apollo/client';
import { onRequestStart, onRequestEnd } from './networkTracker';
import { pageLoadCompleteRef } from './pageLoadState';

const { getSnowplowTracker, HudlDataSchemas } = require('@hudl/analytics');

const pendingEvents = []; // Holds { payload, extraEntities }

/**
 * Helper to create a DurationEvent payload.
 */
const makePayload = (operationName, start, end = Date.now()) =>
  HudlDataSchemas.DurationEvent.V1_0_0.describe({
    event_name: 'graphql_query',
    event_desc: operationName,
    start_timestamp: new Date(start).toISOString(),
    end_timestamp: new Date(end).toISOString(),
    duration_ms: end - start,
    app_version: null,
  });

/**
 * Builds ConditionEntities from GraphQL variables.
 * - Stringifies arrays and objects.
 * - Skips undefined or null values.
 *
 * @param {Object} variables
 * @returns {Array} List of ConditionEntity self-describing events
 */
const buildConditionEntitiesFromGraphQL = (variables = {}) => {
  if (!variables || typeof variables !== 'object') return [];

  return Object.entries(variables).flatMap(([key, value]) => {
    if (value === undefined || value === null) return [];

    const stringifiedValue =
      typeof value === 'object' ? JSON.stringify(value) : String(value);

    return HudlDataSchemas.ConditionEntity.V1_0_2.describe({
      condition_type: key,
      condition_value: stringifiedValue,
      condition_subtype: 'graphql_parameter',
      condition_operator: '=',
      is_pre_selected: true,
    });
  });
};

/**
 * Flushes all pending GraphQL timing events.
 * Attaches the provided pageContexts plus variable entities to each event.
 *
 * @param {Array} pageContexts - List of PageEntity and ConditionEntities for the page.
 */
export const flushPendingTimingEvents = (pageContexts = []) => {
  if (!pendingEvents.length) return;

  try {
    const tracker = getSnowplowTracker('hudlIQ');
    pendingEvents.forEach(({ payload, extraEntities }) => {
      const contexts = [...pageContexts, ...(extraEntities || [])];
      tracker?.trackSelfDescribingEvent(payload, contexts);
    });
    // console.log(`[timingLink] Flushed ${pendingEvents.length} GraphQL events`);
    pendingEvents.length = 0; // Clear after flushing
  } catch (err) {
    // console.warn('[timingLink] Failed to flush GraphQL timings:', err);
  }
};

/**
 * Apollo Link middleware for capturing GraphQL request durations
 * and tracking them in Snowplow with appropriate context.
 */
export const timingLink = new ApolloLink((operation, forward) => {
  const start = Date.now();
  onRequestStart(); // Notify network tracker that a request started

  return new Observable((observer) => {
    const subscription = forward(operation).subscribe({
      next: (result) => {
        try {
          const operationName = operation?.operationName || 'Unnamed';
          const end = Date.now();
          const payload = makePayload(operationName, start, end);

          const variableEntities = buildConditionEntitiesFromGraphQL(
            operation?.variables,
          );

          if (pageLoadCompleteRef.current) {
            const tracker = getSnowplowTracker('hudlIQ');
            tracker?.trackSelfDescribingEvent(payload, variableEntities);
          } else {
            pendingEvents.push({
              payload,
              extraEntities: variableEntities,
            });
          }
        } catch (err) {
          // console.warn('[timingLink] Failed to build GraphQL duration event:', err);
        }

        observer.next(result);
      },
      error: (err) => observer.error(err),
      complete: () => observer.complete(),
    });

    return () => {
      onRequestEnd(); // Notify network tracker that a request ended
      subscription.unsubscribe();
    };
  });
});

// eslint-disable-next-line no-underscore-dangle
export const _testOnly_clearPendingEvents = () => {
  pendingEvents.length = 0;
};
// eslint-disable-next-line no-underscore-dangle
export const _testOnly_getPendingEvents = () => pendingEvents;
// eslint-disable-next-line no-underscore-dangle
export const _testOnly_getPageLoadCompleteRef = () => pageLoadCompleteRef;

export { buildConditionEntitiesFromGraphQL };
